import { useState, type FormEvent } from 'react';
import { Brand } from '../app/Brand';
import { useSyncStatus } from '../sync/status';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { useAuth } from './AuthProvider';

/** Signed out with nothing cached: a full-page sign-in. */
export function SignInScreen() {
  return (
    <main className="signin">
      <section className="signin-hero" aria-hidden="true">
        <Brand />
        <div className="signin-hero-copy">
          <p className="signin-hero-title">Every job, every site, every record.</p>
          <p className="signin-hero-text">Schedules, run sheets, compliance and client records in one place, and it keeps working without reception.</p>
        </div>
        <p className="signin-hero-foot">PCS Pest Control · North Queensland</p>
      </section>
      <section className="signin-main">
        <div className="signin-compact-brand"><Brand /></div>
        <div className="signin-card"><SignInPanel /></div>
      </section>
    </main>
  );
}

/** The session ended while working: sign in again over the workspace, keeping queued changes. */
export function SessionExpiredModal() {
  return (
    <Modal id="loginModal" size="sm" labelledBy="loginTitle" dismissable={false}>
      <SignInPanel />
    </Modal>
  );
}

function SignInPanel() {
  const { signIn, sessionExpired, config, signInError, signInWithProvider } = useAuth();
  const status = useSyncStatus();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try { await signIn(username, password); setPassword(''); setError(''); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Username or password is incorrect.'); }
    finally { setBusy(false); }
  }

  const queued = status.pending + status.conflicts + status.rejected;
  // Before the server's sign-in methods have ever been fetched (first visit while offline), offer the form.
  const providerLogin = Boolean(config?.oidc);
  const localLogin = !config || config.localLogin;
  const google = providerLogin && Boolean(config?.providers?.includes('google'));
  const microsoft = providerLogin && Boolean(config?.providers?.includes('microsoft'));
  const message = error || signInError;

  const form = (
    <form className="signin-form" onSubmit={submit}>
      <label>Username <input id="loginUsername" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} autoFocus={!providerLogin} /></label>
      <label>Password <input id="loginPassword" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
      <button className={`btn ${providerLogin ? 'btn-secondary' : 'btn-primary'} btn-block`} id="signInButton" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
    </form>
  );

  return (
    <div className="signin-panel">
      <header>
        <h1 id="loginTitle">{sessionExpired ? 'Sign in again' : 'Welcome back'}</h1>
        <p className="form-help">
          {sessionExpired
            ? `Your session has ended.${queued ? ` Sign in to sync ${queued} change${queued === 1 ? '' : 's'} saved on this device.` : ' Sign in to carry on where you left off.'}`
            : 'Sign in to the PCS workspace.'}
        </p>
      </header>
      {message && <p className="signin-error" role="alert">{message}</p>}
      {providerLogin && (
        <div className="signin-providers">
          {google && (
            <button className="btn btn-block signin-provider signin-google" id="googleSignInButton" type="button" onClick={() => signInWithProvider('google')} autoFocus>
              <GoogleLogo />Continue with Google
            </button>
          )}
          {microsoft && (
            <button className="btn btn-block signin-provider signin-microsoft" id="microsoftSignInButton" type="button" onClick={() => signInWithProvider('microsoft')} autoFocus={!google}>
              <MicrosoftLogo />Continue with Microsoft
            </button>
          )}
          <button className="btn btn-primary btn-block signin-provider" id="providerSignInButton" type="button" onClick={() => signInWithProvider()} autoFocus={!google && !microsoft}>
            <Icon name="mail" size={18} />Continue with {config?.providerName}
          </button>
        </div>
      )}
      {providerLogin && localLogin && (
        <details className="signin-dev">
          <summary>Use a development account</summary>
          {form}
        </details>
      )}
      {!providerLogin && localLogin && form}
      {providerLogin && <p className="signin-foot">New to PCS? Ask your administrator for an invitation, then sign in with the same email address.</p>}
      <p className="signin-legal"><a href="/privacy">Privacy</a><span aria-hidden="true">·</span><a href="/terms">Terms</a></p>
    </div>
  );
}

/** Microsoft's four-square logo, as its sign-in branding guidelines ask for on a "Continue with Microsoft" button. */
function MicrosoftLogo() {
  return (
    <svg className="signin-microsoft-logo" viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
      <path fill="#F25022" d="M1 1h9v9H1z" />
      <path fill="#7FBA00" d="M11 1h9v9h-9z" />
      <path fill="#00A4EF" d="M1 11h9v9H1z" />
      <path fill="#FFB900" d="M11 11h9v9h-9z" />
    </svg>
  );
}

/** Google's "G" mark, as its sign-in branding guidelines ask for on a "Continue with Google" button. */
function GoogleLogo() {
  return (
    <svg className="signin-google-logo" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
